import { useMemo, useState } from 'react'
import { Button, Form, Table, Badge } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { IMessageTemplate } from '../../../types/campaigns'
import { ChannelBadge } from './ChannelBadge'
import { SUPPORTED_CHANNELS, CHANNEL_LABELS } from '../../../constants/campaigns'

type Props = {
  templates: IMessageTemplate[]
  onCreate: () => void
  onEdit: (template: IMessageTemplate) => void
  onDuplicate: (template: IMessageTemplate) => void
  onToggleBlueprint: (template: IMessageTemplate, isBlueprint: boolean) => void
  canManageBlueprints?: boolean
  canSeeOwner?: boolean
  isSuperAdmin?: boolean
}

const channelOptions: Array<{ value: string; label: string }> = [
  { value: '', label: 'All channels' },
  ...SUPPORTED_CHANNELS.map(channel => ({
    value: channel,
    label: CHANNEL_LABELS[channel]
  }))
]

export const TemplatesPanel = ({
  templates,
  onCreate,
  onEdit,
  onDuplicate,
  onToggleBlueprint,
  canManageBlueprints = false,
  canSeeOwner = false,
  isSuperAdmin = false,
}: Props) => {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [channelFilter, setChannelFilter] = useState('')
  const [blueprintFilter, setBlueprintFilter] = useState<'all' | 'blueprints' | 'regular'>('all')
  const [ownerFilter, setOwnerFilter] = useState('')
  const [orgFilter, setOrgFilter] = useState('')

  const getChannels = (template: IMessageTemplate): string[] => {
    let meta = template.metadata
    if (typeof meta === 'string') {
      try {
        meta = JSON.parse(meta)
      } catch (e) {
        meta = {}
      }
    }

    const enabled = meta?.enabled_channels
    if (Array.isArray(enabled) && enabled.length > 0) {
      return enabled
    }

    return template.channel ? [template.channel] : []
  }

  const ownerOptions = useMemo(() => {
    const seen = new Map<string, string>()
    templates.forEach((t) => {
      if (t.owner) {
        seen.set(t.owner.id, `${t.owner.firstName} ${t.owner.lastName} - ${t.owner.email}`)
      }
    })
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]))
  }, [templates])

  const orgOptions = useMemo(() => {
    const seen = new Map<string, string>()
    templates.forEach((t) => {
      if (t.organization) {
        seen.set(t.organization.id, t.organization.name)
      }
    })
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]))
  }, [templates])

  const filteredTemplates = useMemo(() => {
    return templates.filter((template) => {
      const templateChannels = getChannels(template)
      const matchesChannel = !channelFilter || templateChannels.includes(channelFilter)
      const text = `${template.name} ${template.description ?? ''} ${template.subject ?? ''}`.toLowerCase()
      const matchesSearch = !search || text.includes(search.toLowerCase())
      const matchesBlueprint =
        blueprintFilter === 'all' ||
        (blueprintFilter === 'blueprints' && template.isBlueprint) ||
        (blueprintFilter === 'regular' && !template.isBlueprint)
      const matchesOwner = !ownerFilter || template.owner?.id === ownerFilter
      const matchesOrg = !orgFilter || template.organization?.id === orgFilter
      return matchesChannel && matchesSearch && matchesBlueprint && matchesOwner && matchesOrg
    })
  }, [templates, channelFilter, search, blueprintFilter, ownerFilter, orgFilter])

  const blueprintCount = templates.filter(t => t.isBlueprint).length

  return (
    <div className='card mt-5'>
      <div className='card-header border-0 pt-5 d-flex align-items-center justify-content-between'>
        <div>
          <h4 className='mb-1'>
            Templates
            {blueprintCount > 0 && (
              <Badge bg='warning' text='dark' className='ms-2 fs-8'>
                ⭐ {blueprintCount} blueprint{blueprintCount !== 1 ? 's' : ''}
              </Badge>
            )}
          </h4>
          <span className='text-muted'>Central repository of message templates for every channel.</span>
        </div>
        <Button size='sm' onClick={onCreate} className='btn-primary'>
          Create template
        </Button>
      </div>
      <div className='card-body border-top'>
        <div className='row g-3 mb-4'>
          <div className={canSeeOwner ? 'col-md-4' : 'col-md-5'}>
            <Form.Control
              type='search'
              placeholder='Search by name or content'
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <div className='col-md-2'>
            <Form.Select value={channelFilter} onChange={(event) => setChannelFilter(event.target.value)}>
              {channelOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </div>
          <div className='col-md-2'>
            <Form.Select
              value={blueprintFilter}
              onChange={(event) => setBlueprintFilter(event.target.value as 'all' | 'blueprints' | 'regular')}
            >
              <option value='all'>All templates</option>
              <option value='blueprints'>⭐ Blueprints only</option>
              <option value='regular'>Regular only</option>
            </Form.Select>
          </div>
          {canSeeOwner && (
            <div className='col-md-3'>
              <Form.Select value={ownerFilter} onChange={(event) => setOwnerFilter(event.target.value)}>
                <option value=''>All users</option>
                {ownerOptions.map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </Form.Select>
            </div>
          )}
          {isSuperAdmin && (
            <div className='col-md-3'>
              <Form.Select value={orgFilter} onChange={(event) => setOrgFilter(event.target.value)}>
                <option value=''>All organizations</option>
                {orgOptions.map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </Form.Select>
            </div>
          )}
        </div>
        {filteredTemplates.length === 0 ? (
          <div className='campaigns-placeholder justify-content-start px-0'>
            {templates.length === 0 ? 'No templates yet. Start by creating one.' : 'No templates match the selected filters.'}
          </div>
        ) : (
          <div className='table-responsive'>
            <Table hover className='align-middle mb-0'>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Channels</th>
                  <th>Subject</th>
                  <th>Variables</th>
                  {canSeeOwner && <th>Created by</th>}
                  {isSuperAdmin && <th>Organization</th>}
                  <th>Updated</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredTemplates.map((template) => (
                  <tr key={template.id}>
                    <td>
                      <div className='d-flex align-items-center gap-2'>
                        <div className='fw-semibold'>{template.name}</div>
                        {template.isBlueprint && (
                          <Badge bg='warning' text='dark' title='Blueprint template'>
                            ⭐ Blueprint
                          </Badge>
                        )}
                      </div>
                      <div
                        className='text-muted fs-8'
                        style={{maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}
                        title={template.description || undefined}
                      >
                        {template.description || 'No description'}
                      </div>
                    </td>
                    <td>
                      <div className='d-flex flex-column gap-1 align-items-start'>
                        {getChannels(template).map(ch => (
                          <ChannelBadge key={ch} channel={ch} />
                        ))}
                      </div>
                    </td>
                    <td>
                      <div
                        style={{maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}
                        title={template.subject || undefined}
                      >
                        {template.subject || '—'}
                      </div>
                    </td>
                    <td>
                      <div className='text-muted'>
                        {template.variables && (Array.isArray(template.variables) ? template.variables.length : 0) > 0
                          ? (Array.isArray(template.variables) ? template.variables.join(', ') : template.variables)
                          : '—'}
                      </div>
                    </td>
                    {canSeeOwner && (
                      <td>
                        {template.owner
                          ? `${template.owner.firstName} ${template.owner.lastName}`
                          : '—'}
                      </td>
                    )}
                    {isSuperAdmin && (
                      <td>{template.organization?.name ?? '—'}</td>
                    )}
                    <td>{template.updatedAt ? new Date(template.updatedAt).toLocaleString() : '—'}</td>
                    <td className='text-end'>
                      <Button
                        size='sm'
                        variant={template.isBlueprint ? 'warning' : 'outline-secondary'}
                        onClick={() => onToggleBlueprint(template, !template.isBlueprint)}
                        className='me-2'
                        title={template.isBlueprint ? 'Remove blueprint status' : 'Mark as blueprint'}
                        disabled={!canManageBlueprints}
                      >
                        {template.isBlueprint ? '⭐' : '☆'}
                      </Button>
                      <Button
                        size='sm'
                        variant='light'
                        onClick={() => onDuplicate(template)}
                        className='me-2'
                        title='Duplicate'
                      >
                        <i className='bi bi-files'></i>
                      </Button>
                      <Button
                        size='sm'
                        variant='light'
                        onClick={() => onEdit(template)}
                        className='me-2'
                        disabled={template.isBlueprint && !canManageBlueprints}
                      >
                        Edit
                      </Button>
                      <Button
                        size='sm'
                        variant='primary'
                        onClick={() => navigate(`/templates/edit/${template.id}`)}
                      >
                        {template.isBlueprint && !canManageBlueprints ? 'View Blueprint' : '📝 Variants'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </div>
    </div>
  )
}
